// src/components/organisms/DeleteClientDialog.jsx
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Alert,
  Stack,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { clientService } from "../../api/clientService";

/**
 * DeleteClientDialog — shows related record counts before deleting.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {function} props.onClose
 * @param {function} props.onSuccess
 * @param {object|null} props.client  — the client object (needs .id, .first_name, .last_name)
 */
export default function DeleteClientDialog({ open, onClose, onSuccess, client }) {
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !client) return;
    setInfo(null);
    setError("");
    setLoadingInfo(true);
    clientService.getRelated(client.id)
      .then(setInfo)
      .catch(() => setError("Could not load related records."))
      .finally(() => setLoadingInfo(false));
  }, [open, client]);

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await clientService.delete(client.id, true);
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err.message || "Failed to delete client");
    } finally {
      setDeleting(false);
    }
  };

  if (!client) return null;

  const counts = info?.related_counts || {};
  const hasRelated = counts.deals > 0 || counts.tasks > 0 || counts.notes > 0 || counts.activities > 0;
  const hasActiveDeals = info?.has_active_deals;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <DeleteRoundedIcon color="error" />
          <span>Delete Client</span>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loadingInfo && (
          <Stack alignItems="center" py={2}>
            <CircularProgress size={24} />
          </Stack>
        )}

        {!loadingInfo && info && (
          <>
            {hasActiveDeals && (
              <Alert severity="warning" icon={<WarningAmberRoundedIcon />} sx={{ mb: 2 }}>
                This client has <strong>{counts.active_deals}</strong> active deal(s). Deleting will permanently remove them.
              </Alert>
            )}

            <Typography variant="body2" gutterBottom>
              Are you sure you want to delete <strong>{info.client_name}</strong>?
              {hasRelated && " This will also remove:"}
            </Typography>

            {hasRelated && (
              <List dense disablePadding sx={{ mt: 1 }}>
                {counts.deals > 0 && (
                  <ListItem disableGutters>
                    <ListItemText
                      primary={`${counts.deals} deal(s) — ${counts.active_deals} active`}
                      primaryTypographyProps={{ variant: "body2", color: counts.active_deals > 0 ? "warning.main" : "text.primary" }}
                    />
                  </ListItem>
                )}
                {counts.activities > 0 && (
                  <ListItem disableGutters>
                    <ListItemText primary={`${counts.activities} timeline activities`} primaryTypographyProps={{ variant: "body2" }} />
                  </ListItem>
                )}
                {counts.tasks > 0 && (
                  <ListItem disableGutters>
                    <ListItemText primary={`${counts.tasks} task(s)`} primaryTypographyProps={{ variant: "body2" }} />
                  </ListItem>
                )}
                {counts.notes > 0 && (
                  <ListItem disableGutters>
                    <ListItemText primary={`${counts.notes} note(s)`} primaryTypographyProps={{ variant: "body2" }} />
                  </ListItem>
                )}
                {counts.listings > 0 && (
                  <ListItem disableGutters>
                    <ListItemText
                      primary={`${counts.listings} listing(s) will be unlinked (not deleted)`}
                      primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                    />
                  </ListItem>
                )}
              </List>
            )}

            {!hasRelated && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No associated records will be affected.
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={deleting || loadingInfo}
          startIcon={deleting ? <CircularProgress size={16} /> : <DeleteRoundedIcon />}
        >
          {deleting ? "Deleting…" : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
